/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, cx, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/cross-circle';
import SuccessIcon from '@atlaskit/icon/core/status-success';
import Image from '@atlaskit/image';
import { Box, Flex, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const iconSpacingStyles = cssMap({
	space050: {
		paddingBlock: token('space.050'),
		paddingInline: token('space.050'),
	},
});

const wrapperStyles = cssMap({
	root: {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
	},
	fullWidth: {
		'@media (min-width: 30rem)': {
			gridColumn: 'span 2',
		},
	},
});

const imageContainerStyles = cssMap({
	root: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderStartStartRadius: token('radius.small'),
		borderStartEndRadius: token('radius.small'),
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderBlockEnd: 'none',
		overflow: 'hidden',
		'@media (min-width: 48rem)': {
			minHeight: '10rem',
		},
	},
});

/**
 * This could be a stack and a box
 */
const contentContainerStyles = cssMap({
	root: {
		flexGrow: 1,
		borderBlockStartStyle: 'solid',
		// We can't use the border.width tokens here as they only go up to 2px
		// @ts-ignore
		borderBlockStartWidth: '4px',
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.200'),
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
		borderStartStartRadius: '0',
		borderStartEndRadius: '0',
		borderEndEndRadius: token('radius.small'),
		borderEndStartRadius: token('radius.small'),
	},
});

const appearanceMap = cssMap({
	do: {
		backgroundColor: token('color.background.success'),
		borderColor: token('color.border.success'),
	},
	dont: {
		backgroundColor: token('color.background.danger'),
		borderColor: token('color.border.danger'),
	},
});

export type DoDontProps = {
	type: 'do' | 'dont';
	children: React.ReactNode;
	testId?: string;
	image?: {
		/**
		 * Image URL to use for light mode.
		 */
		url: string;
		/**
		 * Image URL to use for dark mode. This overrides `src` when the user
		 * has selected dark mode either in the app or on their operating system.
		 */
		urlDarkMode?: string;
		alt: string;
	};
	isFullWidth?: boolean;
};

/**
 * __DoDont__
 *
 * A DoDont visually describes suggested and discouraged patterns.
 *
 * Should be wrapped in a DoDontGrid.
 *
 * @example
 * ```tsx
 * // Two DoDonts side by side, taking up half the row each
 * <DoDontGrid>
 *   <DoDont type="do" />
 *   <DoDont type="dont" />
 * </DoDontGrid>
 *
 * // Single DoDont, taking up half the row
 * <DoDontGrid>
 *   <DoDont type="do" />
 * </DoDontGrid>
 *
 * // Single DoDont, taking up the full width of the row
 * <DoDontGrid>
 *   <DoDont type="do" isFullWidth />
 * </DoDontGrid>
 *
 * // Multiple rows of DoDonts (combination)
 * <DoDontGrid>
 *   <DoDont type="do" />
 *   <DoDont type="dont" />
 *   <DoDont type="do" isFullWidth />
 *   <DoDont type="dont" />
 *   <DoDont type="do" />
 * </DoDontGrid>
 * ```
 */
export const DoDont: ({
	type,
	children,
	testId,
	image,
	isFullWidth,
}: DoDontProps) => JSX.Element = ({ type, children, testId, image, isFullWidth }: DoDontProps) => {
	return (
		<Box testId={testId} xcss={cx(wrapperStyles.root, isFullWidth && wrapperStyles.fullWidth)}>
			{image && (
				<Box xcss={imageContainerStyles.root}>
					<Image src={image.url} srcDark={image.urlDarkMode} alt={image.alt || ''} />
				</Box>
			)}
			<Stack space="space.150" xcss={cx(contentContainerStyles.root, appearanceMap[type])}>
				<Inline alignBlock="center" space="space.100">
					{type === 'do' ? (
						<Flex xcss={iconSpacingStyles.space050}>
							<SuccessIcon label="" color={token('color.icon.success')} />
						</Flex>
					) : (
						<Flex xcss={iconSpacingStyles.space050}>
							<CrossIcon label="" color={token('color.icon.danger')} />
						</Flex>
					)}
					<Heading as="h4" size="small">
						{type === 'do' ? 'Do' : 'Don’t'}
					</Heading>
				</Inline>
				<Text as="p">{children}</Text>
			</Stack>
		</Box>
	);
};
